// Быстрый ввод с «Сегодня»: вес, давление, самочувствие, питание за день — одним окном.

import { useState, type FormEvent } from 'react'

import { ApiError } from '@/api/client'
import { Button, ErrorText, Input, Modal, Tabs, useToast } from '@/components/ui'
import { parseDecimal } from '@/lib/format'

import { todayApi, useTodayMutation } from './api'
import { WELLBEING_LABELS } from './logic'

export type QuickKind = 'weight' | 'pressure' | 'wellbeing' | 'nutrition'

const TABS = [
  { value: 'weight', label: 'Вес' },
  { value: 'pressure', label: 'Давление' },
  { value: 'wellbeing', label: 'Самочувствие' },
  { value: 'nutrition', label: 'Питание' },
] as const

function fieldError(error: unknown, ...fields: string[]): string | null {
  return error instanceof ApiError && error.field && fields.includes(error.field) ? error.message : null
}

function formError(error: unknown, ...fields: string[]): unknown {
  return error instanceof ApiError && error.field && fields.includes(error.field) ? null : error
}

type FormProps = { day: string; done: (message: string) => void }

function WeightForm({ done }: FormProps) {
  const [value, setValue] = useState('')
  const [local, setLocal] = useState<string | null>(null)
  const save = useTodayMutation(todayApi.addWeight)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const parsed = parseDecimal(value)
    if (parsed === null) return setLocal('Введи число')
    setLocal(null)
    save.mutate(parsed, { onSuccess: () => done('Вес записан') })
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <Input label="Вес" inputMode="decimal" suffix="кг" autoFocus value={value} onChange={(e) => setValue(e.target.value)} error={local ?? fieldError(save.error, 'value')} />
      <ErrorText error={formError(save.error, 'value')} />
      <Button type="submit" variant="primary" loading={save.isPending}>
        Сохранить
      </Button>
    </form>
  )
}

function PressureForm({ done }: FormProps) {
  const [sys, setSys] = useState('')
  const [dia, setDia] = useState('')
  const [pulse, setPulse] = useState('')
  const [local, setLocal] = useState<string | null>(null)
  const save = useTodayMutation((v: { s: number; d: number; p: number | null }) => todayApi.addPressure(v.s, v.d, v.p))
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const s = parseDecimal(sys)
    const d = parseDecimal(dia)
    if (s === null || d === null) return setLocal('Введи верхнее и нижнее давление')
    setLocal(null)
    save.mutate({ s, d, p: parseDecimal(pulse) }, { onSuccess: () => done('Давление записано') })
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="grid grid-cols-3 gap-3">
        <Input label="Верхнее" inputMode="numeric" autoFocus value={sys} onChange={(e) => setSys(e.target.value)} error={local ?? fieldError(save.error, 'systolic')} />
        <Input label="Нижнее" inputMode="numeric" value={dia} onChange={(e) => setDia(e.target.value)} error={fieldError(save.error, 'diastolic')} />
        <Input label="Пульс" inputMode="numeric" value={pulse} onChange={(e) => setPulse(e.target.value)} error={fieldError(save.error, 'pulse')} />
      </div>
      <ErrorText error={formError(save.error, 'systolic', 'diastolic', 'pulse')} />
      <Button type="submit" variant="primary" loading={save.isPending}>
        Сохранить
      </Button>
    </form>
  )
}

function WellbeingForm({ day, done }: FormProps) {
  const [mood, setMood] = useState(0)
  const [energy, setEnergy] = useState(0)
  const [local, setLocal] = useState<string | null>(null)
  const save = useTodayMutation((v: { w: number; e: number | null }) => todayApi.saveWellbeing(day, v.w, v.e))
  const scale = (label: string, value: number, set: (n: number) => void) => (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 text-sm font-medium text-fg">{label}</legend>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={value === n}
            aria-label={`${label}: ${n} — ${WELLBEING_LABELS[n]}`}
            onClick={() => set(n)}
            className={`min-h-11 flex-1 rounded-lg border text-sm font-semibold tabular-nums ${
              value === n ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-surface text-fg'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </fieldset>
  )
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!mood) return setLocal('Выбери самочувствие от 1 до 5')
    setLocal(null)
    save.mutate({ w: mood, e: energy || null }, { onSuccess: () => done('Самочувствие записано') })
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {scale('Самочувствие', mood, setMood)}
      {scale('Энергия', energy, setEnergy)}
      <ErrorText error={local ?? save.error} />
      <Button type="submit" variant="primary" loading={save.isPending}>
        Сохранить
      </Button>
    </form>
  )
}

const NUTRITION_FIELDS = [
  { key: 'kcal', label: 'Калории', suffix: 'ккал' },
  { key: 'protein_g', label: 'Белки', suffix: 'г' },
  { key: 'fat_g', label: 'Жиры', suffix: 'г' },
  { key: 'carbs_g', label: 'Углеводы', suffix: 'г' },
  { key: 'water_ml', label: 'Вода', suffix: 'мл' },
] as const

function NutritionForm({ day, done }: FormProps) {
  const [values, setValues] = useState<Record<string, string>>({})
  const [local, setLocal] = useState<string | null>(null)
  const save = useTodayMutation((data: Record<string, number | null>) => todayApi.saveNutrition(day, data))
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const data: Record<string, number | null> = {}
    for (const f of NUTRITION_FIELDS) {
      const parsed = parseDecimal(values[f.key] ?? '')
      if (parsed !== null) data[f.key] = parsed
    }
    if (Object.keys(data).length === 0) return setLocal('Укажи хотя бы одно значение')
    setLocal(null)
    save.mutate(data, { onSuccess: () => done('Питание записано') })
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="grid grid-cols-2 gap-3">
        {NUTRITION_FIELDS.map((f, i) => (
          <Input
            key={f.key}
            label={f.label}
            suffix={f.suffix}
            inputMode="decimal"
            autoFocus={i === 0}
            value={values[f.key] ?? ''}
            onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
            error={fieldError(save.error, f.key)}
          />
        ))}
      </div>
      <ErrorText error={local ?? formError(save.error, ...NUTRITION_FIELDS.map((f) => f.key))} />
      <Button type="submit" variant="primary" loading={save.isPending}>
        Сохранить
      </Button>
    </form>
  )
}

export function QuickEntry({
  open,
  onClose,
  day,
  initial = 'weight',
}: {
  open: boolean
  onClose: () => void
  day: string
  initial?: QuickKind
}) {
  const toast = useToast()
  const [kind, setKind] = useState<QuickKind>(initial)
  const done = (message: string) => {
    toast.show({ message, tone: 'accent' })
    onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title="Быстрый ввод">
      <div className="flex flex-col gap-4">
        <Tabs items={TABS} value={kind} onChange={setKind} label="Что записать" />
        {kind === 'weight' ? <WeightForm day={day} done={done} /> : null}
        {kind === 'pressure' ? <PressureForm day={day} done={done} /> : null}
        {kind === 'wellbeing' ? <WellbeingForm day={day} done={done} /> : null}
        {kind === 'nutrition' ? <NutritionForm day={day} done={done} /> : null}
      </div>
    </Modal>
  )
}
